import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { Suspense, lazy } from "react";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import LoginPage from "@/pages/LoginPage";
import HomePage from "@/pages/HomePage";
import ChecklistRunPage from "@/pages/ChecklistRunPage";
import MonthlyTrackerPage from "@/pages/MonthlyTrackerPage";
import TemplateEditorPage from "@/pages/TemplateEditorPage";
import UsersAdminPage from "@/pages/UsersAdminPage";
import OutboxPage from "@/pages/OutboxPage";
import ServiceRequestsPage from "@/pages/ServiceRequestsPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import AttendancePage from "@/pages/AttendancePage";
import RosterPage from "@/pages/RosterPage";
import AttendanceMonthlyPage from "@/pages/AttendanceMonthlyPage";
import StorageStatsPage from "@/pages/StorageStatsPage";
import AbsenceRulesPage from "@/pages/AbsenceRulesPage";
import CatalogPage from "@/pages/CatalogPage";
import ConsumablesPage from "@/pages/ConsumablesPage";
import PurchaseRequestsPage from "@/pages/PurchaseRequestsPage";
import ReconciliationPage from "@/pages/ReconciliationPage";
import UsageLogsPage from "@/pages/UsageLogsPage";
import SRDetailPage from "@/pages/SRDetailPage";
import SLALookupPage, { SLAEditorPage } from "@/pages/SLALookupPage";
import CompliancePage from "@/pages/CompliancePage";
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));import TrendsPage from "@/pages/TrendsPage";
const AuditLogPage = lazy(() => import("@/pages/AuditLogPage"));
import ReportsListPage from "@/pages/ReportsListPage";
import ReportEditorPage from "@/pages/ReportEditorPage";
import PublicReportPage from "@/pages/PublicReportPage";
import ProcurementDashboardPage from "@/pages/ProcurementDashboardPage";
import FhReportsAdminPage from "@/pages/FhReportsAdminPage";
import ClientDashboardPage from "@/pages/ClientDashboardPage";
import UploadCentrePage from "@/pages/UploadCentrePage";
import BackupsPage from "@/pages/BackupsPage";
import DocumentsPage from "@/pages/DocumentsPage";
import MessagingPage from "@/pages/MessagingPage";
import PresetManagerPage from "@/pages/PresetManagerPage";
import TrainingListPage from "@/pages/TrainingListPage";
import CourseBuilderPage from "@/pages/CourseBuilderPage";
import CourseTakePage from "@/pages/CourseTakePage";
import CoachingQueuePage from "@/pages/CoachingQueuePage";
import CoachingMappingPage from "@/pages/CoachingMappingPage";
import DiaryPage from "@/pages/DiaryPage";
import SiteSettingsPage from "@/pages/SiteSettingsPage";
import ExpensesPage from "@/pages/ExpensesPage";
import ExpenseMonthReportPage from "@/pages/ExpenseMonthReportPage";
import BillableLogPage from "@/pages/BillableLogPage";
import VendorsAdminPage from "@/pages/VendorsAdminPage";
import ExpenseImportPage from "@/pages/ExpenseImportPage";
const ExpenseDashboardPage = lazy(() => import("@/pages/ExpenseDashboardPage"));
import ProcurementPlannerPage from "@/pages/ProcurementPlannerPage";
const SimulatorPage = lazy(() => import("@/pages/SimulatorPage"));
import BuildingOpsPage from "@/pages/BuildingOpsPage";
import CoworkingPage from "@/pages/CoworkingPage";
import CommandCentrePage from "@/pages/CommandCentrePage";
import KraPage from "@/pages/KraPage";
import RoleDashboardPage from "@/pages/RoleDashboardPage";
import BillingStatementPage from "@/pages/BillingStatementPage";
import RequisitionsAdminPage, { SiteRequisitionPage } from "@/pages/RequisitionsAdminPage";
import ReviewFlagsPage from "@/pages/ReviewFlagsPage";
import ImportSummaryPage from "@/pages/ImportSummaryPage";
import IssueLogPage from "@/pages/IssueLogPage";
import PublicRatingPage from "@/pages/PublicRatingPage";
import TutorialIndexPage from "@/pages/TutorialIndexPage";
import ReadinessPage from "@/pages/ReadinessPage";
import LeavesPage from "@/pages/LeavesPage";
import LoansPage from "@/pages/LoansPage";
import StaffProfilePage from "@/pages/StaffProfilePage";
import OtPage from "@/pages/OtPage";
const AgencyReconciliationPage = lazy(() => import("@/pages/AgencyReconciliationPage"));
import AssetsPage from "@/pages/AssetsPage";
import AssetDetailPage from "@/pages/AssetDetailPage";
import HandoversPage from "@/pages/HandoversPage";
import VisitorsPage from "@/pages/VisitorsPage";
import LandingPage from "@/pages/LandingPage";
import WhiteboardPage from "@/pages/WhiteboardPage";
import FundWalletPage from "@/pages/FundWalletPage";
import FundLeaguePage from "@/pages/FundLeaguePage";
import FundFhConsolePage from "@/pages/FundFhConsolePage";
import FundQuizPage from "@/pages/FundQuizPage";

function MustChangePwdGate({ children }) {
    const { user } = useAuth();
    if (user && user.must_change_password) return <Navigate to="/change-password" replace />;
    return children;
}

/**
 * "/" is public — unauthenticated visitors see the marketing landing page;
 * authenticated users get their existing site-grid dashboard (HomePage).
 * We handle it inline instead of ProtectedRoute so the LandingPage renders
 * without redirecting to /login.
 */
function RootRoute() {
    const { user } = useAuth();
    // Still resolving auth state — render nothing to avoid a flash.
    if (user === null) return <div className="min-h-screen bg-[#F4F6F9]" data-testid="root-auth-loading" />;
    if (!user) return <LandingPage />;
    if (user.must_change_password) return <Navigate to="/change-password" replace />;
    return <Layout><HomePage /></Layout>;
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <Toaster position="top-right" richColors />
                    <Suspense fallback={<div className="p-8 text-center text-sm text-gray-500" data-testid="lazy-loading">Loading…</div>}>
                    <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/r/:token" element={<PublicReportPage />} />
                        <Route path="/fund/wallet" element={<ProtectedRoute><MustChangePwdGate><Layout><FundWalletPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/fund/league" element={<ProtectedRoute><MustChangePwdGate><Layout><FundLeaguePage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/fund/fh" element={<ProtectedRoute><MustChangePwdGate><Layout><FundFhConsolePage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/fund/training" element={<ProtectedRoute><MustChangePwdGate><Layout><FundQuizPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route
                            path="/announcements"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <WhiteboardPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/procurement"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <ProcurementDashboardPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/fh-reports"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <FhReportsAdminPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/client"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <ClientDashboardPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/upload-centre"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <UploadCentrePage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/change-password"
                            element={
                                <ProtectedRoute>
                                    <Layout>
                                        <ChangePasswordPage />
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/"
                            element={<RootRoute />}
                        />
                        <Route
                            path="/home"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <RoleDashboardPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/sites/:siteId/run"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <ChecklistRunPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/sites/:siteId/runs/:runId"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <ChecklistRunPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/sites/:siteId/consumables"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <ConsumablesPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/sites/:siteId/reconciliation"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <ReconciliationPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/sites/:siteId/purchase"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <PurchaseRequestsPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/sites/:siteId/usage"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <UsageLogsPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/catalog"
                            element={
                                <ProtectedRoute roles={["HO_ADMIN"]}>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <CatalogPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/purchase"
                            element={
                                <ProtectedRoute roles={["HO_ADMIN", "PROJECT_MANAGER"]}>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <PurchaseRequestsPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/sites/:siteId/tracker"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <MonthlyTrackerPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/sites/:siteId/attendance"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <AttendancePage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/sites/:siteId/roster"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <RosterPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/sites/:siteId/attendance/month"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <AttendanceMonthlyPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/storage"
                            element={
                                <ProtectedRoute roles={["HO_ADMIN"]}>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <StorageStatsPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/absence-rules"
                            element={
                                <ProtectedRoute roles={["HO_ADMIN"]}>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <AbsenceRulesPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/service-requests"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <ServiceRequestsPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/service-requests/:srId"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <SRDetailPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/sla"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <SLALookupPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/sla/:siteId"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <SLALookupPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/sla-editor"
                            element={
                                <ProtectedRoute roles={["HO_ADMIN"]}>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <SLAEditorPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/compliance"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <CompliancePage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute roles={["HO_ADMIN", "PROJECT_MANAGER"]}>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <DashboardPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/trends"
                            element={
                                <ProtectedRoute roles={["HO_ADMIN", "PROJECT_MANAGER"]}>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <TrendsPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/audit-log"
                            element={
                                <ProtectedRoute roles={["HO_ADMIN"]}>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <AuditLogPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/reports"
                            element={
                                <ProtectedRoute roles={["HO_ADMIN"]}>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <ReportsListPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/reports/:reportId"
                            element={
                                <ProtectedRoute roles={["HO_ADMIN"]}>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <ReportEditorPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/backups"
                            element={
                                <ProtectedRoute roles={["HO_ADMIN"]}>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <BackupsPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/documents"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <DocumentsPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/sites/:siteId/documents"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <DocumentsPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/messages"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <MessagingPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/users"
                            element={
                                <ProtectedRoute roles={["HO_ADMIN"]}>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <UsersAdminPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/templates"
                            element={
                                <ProtectedRoute roles={["HO_ADMIN"]}>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <TemplateEditorPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/presets"
                            element={
                                <ProtectedRoute roles={["HO_ADMIN"]}>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <PresetManagerPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/training" element={<ProtectedRoute><MustChangePwdGate><Layout><TrainingListPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/training/:courseId" element={<ProtectedRoute><MustChangePwdGate><Layout><CourseTakePage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/admin/courses/:courseId" element={<ProtectedRoute roles={["HO_ADMIN"]}><MustChangePwdGate><Layout><CourseBuilderPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/coaching" element={<ProtectedRoute><MustChangePwdGate><Layout><CoachingQueuePage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/admin/coaching-mapping" element={<ProtectedRoute roles={["HO_ADMIN"]}><MustChangePwdGate><Layout><CoachingMappingPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/sites/:siteId/diary" element={<ProtectedRoute><MustChangePwdGate><Layout><DiaryPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/admin/sites/:siteId/settings" element={<ProtectedRoute roles={["HO_ADMIN"]}><MustChangePwdGate><Layout><SiteSettingsPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/diary" element={<ProtectedRoute><MustChangePwdGate><Layout><DiaryPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/sites/:siteId/expenses" element={<ProtectedRoute><MustChangePwdGate><Layout><ExpensesPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/sites/:siteId/expenses/report" element={<ProtectedRoute><MustChangePwdGate><Layout><ExpenseMonthReportPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/sites/:siteId/billable-log" element={<ProtectedRoute><MustChangePwdGate><Layout><BillableLogPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/admin/vendors" element={<ProtectedRoute roles={["HO_ADMIN"]}><MustChangePwdGate><Layout><VendorsAdminPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/admin/expenses/import" element={<ProtectedRoute roles={["HO_ADMIN"]}><MustChangePwdGate><Layout><ExpenseImportPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/admin/expense-dashboard" element={<ProtectedRoute roles={["HO_ADMIN", "PROJECT_MANAGER"]}><MustChangePwdGate><Layout><ExpenseDashboardPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/admin/procurement" element={<ProtectedRoute roles={["HO_ADMIN"]}><MustChangePwdGate><Layout><ProcurementPlannerPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/admin/simulator" element={<ProtectedRoute roles={["HO_ADMIN"]}><MustChangePwdGate><Layout><SimulatorPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route
                            path="/admin/outbox"
                            element={
                                <ProtectedRoute roles={["HO_ADMIN"]}>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <OutboxPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/buildings/:siteId"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <BuildingOpsPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/coworking/:siteId"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <CoworkingPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/command"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <CommandCentrePage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/kra"
                            element={
                                <ProtectedRoute>
                                    <MustChangePwdGate>
                                        <Layout>
                                            <KraPage />
                                        </Layout>
                                    </MustChangePwdGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/public/rate/:siteId/:month/:token" element={<PublicRatingPage kind="rating" />} />
                        <Route path="/public/feedback/:siteId/:token" element={<PublicRatingPage kind="feedback" />} />
                        <Route path="/admin/requisitions" element={<ProtectedRoute><MustChangePwdGate><Layout><RequisitionsAdminPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/admin/review-flags" element={<ProtectedRoute><MustChangePwdGate><Layout><ReviewFlagsPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/admin/import-summary" element={<ProtectedRoute><MustChangePwdGate><Layout><ImportSummaryPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/sites/:siteId/requisition" element={<ProtectedRoute><MustChangePwdGate><Layout><SiteRequisitionPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/sites/:siteId/issue-log" element={<ProtectedRoute><MustChangePwdGate><Layout><IssueLogPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/sites/:siteId/billing-statement" element={<ProtectedRoute><MustChangePwdGate><Layout><BillingStatementPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/tutorial" element={<ProtectedRoute><MustChangePwdGate><Layout><TutorialIndexPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/readiness" element={<ProtectedRoute><MustChangePwdGate><Layout><ReadinessPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/leaves" element={<ProtectedRoute><MustChangePwdGate><Layout><LeavesPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/loans" element={<ProtectedRoute><MustChangePwdGate><Layout><LoansPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/staff/:staffId" element={<ProtectedRoute><MustChangePwdGate><Layout><StaffProfilePage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/ot" element={<ProtectedRoute><MustChangePwdGate><Layout><OtPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/reconciliation" element={<ProtectedRoute><MustChangePwdGate><Layout><AgencyReconciliationPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/assets" element={<ProtectedRoute><MustChangePwdGate><Layout><AssetsPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/assets/:assetId" element={<ProtectedRoute><MustChangePwdGate><Layout><AssetDetailPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/handovers" element={<ProtectedRoute><MustChangePwdGate><Layout><HandoversPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="/visitors" element={<ProtectedRoute><MustChangePwdGate><Layout><VisitorsPage /></Layout></MustChangePwdGate></ProtectedRoute>} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    </Suspense>
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}

export default App;
